import React, { useState, useEffect } from 'react';
import { s3IconStyles } from './S3IconStyles';
import { S3IconProps } from './S3Icon.types';
import { API_BASE_URL } from '../../api/config';

export const S3Icon: React.FC<S3IconProps> = ({
    iconName,
    color = 'currentColor',
    width = 24,
    height = 24,
    className = '',
    style = {},
    preserveBackground = false,
}) => {
    const [svgContent, setSvgContent] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchIcon = async () => {
            try {
                setLoading(true);
                setError(null);

                const iconUrl = `${API_BASE_URL}/api/v1/files/icons/${iconName}.svg`;
                const response = await fetch(iconUrl);
                if (!response.ok) throw new Error(`Failed to fetch icon: ${response.status}`);

                const svgText = await response.text();
                setSvgContent(svgText);
            } catch (error) {
                setError(error instanceof Error ? error.message : 'Unknown error');
            } finally {
                setLoading(false);
            }
        };

        fetchIcon();
    }, [iconName]);

    const processSvg = (svgString: string, newColor: string) => {
        try {
            const parser = new DOMParser();
            const svgDoc = parser.parseFromString(svgString, 'image/svg+xml');
            const svgElement = svgDoc.documentElement;

            if (!svgElement || svgElement.tagName.toLowerCase() !== 'svg') {
                return null;
            }

            svgElement.setAttribute('width', width.toString());
            svgElement.setAttribute('height', height.toString());

            const isBackgroundElement = (el: Element) => {
                return el.tagName.toLowerCase() === 'rect' &&
                    el.getAttribute('width') === '20' &&
                    el.getAttribute('height') === '20' &&
                    el.getAttribute('rx') === '8';
            };

            const processElement = (element: Element, isBackground = false) => {
                if (preserveBackground && isBackground) {
                    return;
                }

                if (element.hasAttribute('fill')) {
                    const currentFill = element.getAttribute('fill');
                    if (currentFill !== 'none') {
                        element.setAttribute('fill', isBackground ? 'transparent' : newColor);
                    }
                } else if (!isBackground) {
                    element.setAttribute('fill', newColor);
                }

                if (element.hasAttribute('stroke') && element.getAttribute('stroke') !== 'none') {
                    element.setAttribute('stroke', newColor);
                }

                Array.from(element.children).forEach(child => {
                    processElement(child, isBackground || isBackgroundElement(child));
                });
            };

            processElement(svgElement, isBackgroundElement(svgElement));

            if (className) {
                const existingClass = svgElement.getAttribute('class') || '';
                svgElement.setAttribute('class', `${existingClass} ${className}`.trim());
            }

            const existingStyle = svgElement.getAttribute('style') || '';
            const combinedStyle = Object.entries(style)
                .map(([key, value]) => `${key}:${value};`)
                .join('');
            svgElement.setAttribute('style', `${existingStyle} ${combinedStyle}`.trim());

            const serializer = new XMLSerializer();
            return serializer.serializeToString(svgElement);
        } catch (error) {
            return null;
        }
    };

    if (loading) return <div className={`s3-icon-loading ${className}`} style={s3IconStyles.loadingIcon(width, height, style)} />;
    if (error) return <div className={`s3-icon-error ${className}`} style={s3IconStyles.errorIcon(width, height, style)}>⚠️</div>;
    if (!svgContent) return null;

    const processedSvg = processSvg(svgContent, color);
    if (!processedSvg) return null;

    return <div dangerouslySetInnerHTML={{ __html: processedSvg }} />;
};

